// BookSearch.js — ПОЛНЫЙ КОМПОНЕНТ (логика + РЕНДЕР)
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Dropdown,
    Form, InputGroup,
    Pagination,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { fetchBooksWithParams, fetchBookById } from "../../http/bookAPI";
import EditBookAdmin from "./EditBookAdmin";
import AdminAmazonAvailabilityAudit from "./AdminAmazonAvailabilityAudit";

const formatMbShort = (bytes) => {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) return "-";
    return `${(value / 1024 / 1024).toFixed(value >= 10 * 1024 * 1024 ? 1 : 2)} MB`;
};

const BookSearch = ({
    externalFilter,
    onOpenUserBooks,
    onOpenBookAssignments,
    onOpenUserAuthorAssignments,
    onOpenUserReviewerAssignments,
}) => {
    // ===== Фильтры =====
    const [status, setStatus] = useState("");
    const [author, setAuthor] = useState("");
    const [ownerId, setOwnerId] = useState("");
    const [bookId, setBookId] = useState("");

    // Поиск по ключу
    const [q, setQ] = useState("");
    const skipNextLoadRef = useRef(true);


    // Дата создания от/до (Date | null)
    const [createdFrom, setCreatedFrom] = useState(null);
    const [createdTo, setCreatedTo] = useState(null);

    // Флаги форматов/состояний
    const [booleanFilters, setBooleanFilters] = useState({
        message_flag: false,
        paper_back_flag: false,
        kindle_flag: false,
        kindle_unlimited_flag: false,
        hard_cover_flag: false,
        audio_flag: false,
        has_cover_file: false,
        has_book_file: false,
    });

    const handleCheckboxChange = useCallback((flag) => {
        setBooleanFilters((prev) => ({ ...prev, [flag]: !prev[flag] }));
    }, []);

    // ===== Пагинация =====
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [count, setCount] = useState(0);
    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(count / Math.max(1, limit))),
        [count, limit]
    );

    // ===== Данные =====
    const [books, setBooks] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    // ===== Модалка =====
    const [selectedBook, setSelectedBook] = useState(null);
    const [selectedDuplicateInfo, setSelectedDuplicateInfo] = useState(null);
    const [modalShow, setModalShow] = useState(false);

    const handleEditClick = useCallback((book, duplicateInfo = null) => {
        setSelectedBook(book);
        setSelectedDuplicateInfo(duplicateInfo);
        setModalShow(true);
    }, []);

    const refetchOneBook = useCallback(async (id) => {
        try {
            const fresh = await fetchBookById(id);
            if (!fresh) return;
            setBooks((prev) => prev.map((b) => (b.id === id ? fresh : b)));
            setSelectedBook((prev) => (prev && prev.id === id ? fresh : prev));
        } catch (e) {
            console.error("Refetch book failed:", e);
        }
    }, []);

    // ===== Даты -> минуты =====
    const toMinutesStartOfDay = (d) => {
        if (!d) return undefined;
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return Math.floor(x.getTime() / 60000);
    };
    const toMinutesEndOfDay = (d) => {
        if (!d) return undefined;
        const x = new Date(d);
        x.setHours(23, 59, 59, 999);
        return Math.floor(x.getTime() / 60000);
    };

    // Параметры запроса
    const buildQueryParams = useCallback(() => {
        const base = {
            page,
            limit,
            q: q?.trim() || undefined,
            status: status || undefined,
            author: author || undefined,
            ownerId: ownerId || undefined,
            bookId: bookId || undefined,
            created_from: toMinutesStartOfDay(createdFrom),
            created_to: toMinutesEndOfDay(createdTo),
        };
        const enabledFlags = Object.fromEntries(
            Object.entries(booleanFilters).filter(([, v]) => v === true)
        );
        return { ...base, ...enabledFlags };
    }, [
        page,
        limit,
        q,
        status,
        author,
        ownerId,
        bookId,
        createdFrom,
        createdTo,
        booleanFilters,
    ]);

    // Загрузка
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = await fetchBooksWithParams(buildQueryParams());
            const items = payload?.items || [];
            setBooks(items);
            setDuplicates(payload?.duplicates || []);
            setCount(Number(payload?.count || 0));
            setMessage(items.length === 0 ? "No books found" : "");
        } catch (error) {
            console.error("Error fetching books:", error);
            setBooks([]);
            setDuplicates([]);
            setCount(0);
            setMessage("Failed to fetch books");
            toast.error("Error fetching books", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQueryParams]);

    const applyFilter = useCallback(async () => {
        setPage(1);
        setLoading(true);
        try {
            const p = await fetchBooksWithParams({ ...buildQueryParams(), page: 1 });
            const items = p?.items || [];
            setBooks(items);
            setDuplicates(p?.duplicates || []);
            setCount(Number(p?.count || 0));
            setMessage(items.length === 0 ? "No books found" : "");
        } catch (e) {
            console.error(e);
            toast.error("Failed to apply filter", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQueryParams]);

    const resetFilters = useCallback(() => {

            skipNextLoadRef.current = true;
            setStatus("");
            setAuthor("");
            setOwnerId("");
            setBookId("");
            setQ("");
            setCreatedFrom(null);
            setCreatedTo(null);
            setBooleanFilters({
            message_flag: false,
            paper_back_flag: false,
            kindle_flag: false,
            kindle_unlimited_flag: false,
            hard_cover_flag: false,
            audio_flag: false,
            has_cover_file: false,
            has_book_file: false,
            });
        setPage(1);           // не триггерим загрузку из-за skip-флага
        setBooks([]);
        setDuplicates([]);
        setCount(0);
        setMessage("");
        setLoading(false);
        }, []);

    useEffect(() => {
        if (!externalFilter?.ownerId) return undefined;

        const nextOwnerId = String(externalFilter.ownerId);
        let alive = true;

        skipNextLoadRef.current = page !== 1;
        setStatus("");
        setAuthor("");
        setOwnerId(nextOwnerId);
        setBookId("");
        setQ("");
        setCreatedFrom(null);
        setCreatedTo(null);
        setBooleanFilters({
            message_flag: false,
            paper_back_flag: false,
            kindle_flag: false,
            kindle_unlimited_flag: false,
            hard_cover_flag: false,
            audio_flag: false,
            has_cover_file: false,
            has_book_file: false,
        });
        setPage(1);
        setMessage("");
        setLoading(true);

        fetchBooksWithParams({ ownerId: nextOwnerId, page: 1, limit })
            .then((payload) => {
                if (!alive) return;
                const items = payload?.items || [];
                setBooks(items);
                setDuplicates(payload?.duplicates || []);
                setCount(Number(payload?.count || 0));
                setMessage(items.length === 0 ? "No books found" : "");
            })
            .catch((error) => {
                if (!alive) return;
                console.error("Error fetching user books:", error);
                setBooks([]);
                setDuplicates([]);
                setCount(0);
                setMessage("Failed to fetch books");
                toast.error("Error fetching books", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [externalFilter?.nonce, externalFilter?.ownerId, limit]);

    const handlePageChange = useCallback(
        (nextPage) => {
            const p = Math.max(1, Math.min(nextPage, totalPages || 1));
            if (p === page) return;
            setPage(p);
        },
        [page, totalPages]
    );

    const handleLimitChange = useCallback(
        (newLimit) => {
            const l = Math.max(1, Math.min(newLimit || 50, 200));
            if (l === limit) return;
            setLimit(l);
            setPage(1);
        },
        [limit]
    );

    const handleModalClose = useCallback(async () => {
        setModalShow(false);
        setSelectedBook(null);
        setSelectedDuplicateInfo(null);
        try {
            setLoading(true);
            const payload = await fetchBooksWithParams(buildQueryParams());
            setBooks(payload?.items || []);
            setDuplicates(payload?.duplicates || []);
            setCount(Number(payload?.count || 0));
            setMessage(
                (payload?.items?.length ?? 0) === 0 ? "No books found" : ""
            );
        } catch (e) {
            console.error(e);
            toast.error("Failed to refresh books after closing modal", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQueryParams]);

    useEffect(() => {
        if (skipNextLoadRef.current) {
            // один раз пропускаем загрузку, например после Reset
            skipNextLoadRef.current = false;
            return;
        }
        loadData();
    }, [page, limit, loadData]);

    // ====== РЕНДЕР ======
    return (
        <>
            <Card className="shadow-sm bg-light p-0">
                <Card.Body>
                    {/* ФИЛЬТРЫ — всё в одну строку, компактно как в AssignmentSearch */}



                    <div className="d-flex flex-wrap gap-2 mb-3 align-items-end justify-content-start">
                        {/* Кнопки */}
                        <div style={{ width: 120 }}>
                        <Dropdown onSelect={(value) => setStatus(value || "")}>
                            <Dropdown.Toggle variant="secondary" size="sm" style={{ width: "100%" }}>
                                {status || "Status"}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {/* Оставляем свободный выбор: можно расширить списком статусов из БД */}
                                <Dropdown.Item eventKey="NEW">NEW</Dropdown.Item>
                                <Dropdown.Item eventKey="LIVE">LIVE</Dropdown.Item>
                                <Dropdown.Item eventKey="PAY_DEL">PAY_DEL</Dropdown.Item>
                                <Dropdown.Item eventKey="NEEDFIX">NEEDFIX</Dropdown.Item>
                                <Dropdown.Item eventKey="DELETED">DELETED</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        </div>
                        <Button variant="success" size="sm" onClick={applyFilter} disabled={loading}>
                            Apply Filter
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={resetFilters} disabled={loading}>
                            Reset
                        </Button>

                        {/* Поисковая строка q */}
                        <div style={{ width: 140 }} className="d-none d-md-block">
                            <InputGroup size="sm">
                                <InputGroup.Text>S</InputGroup.Text>
                                <Form.Control
                                    placeholder="Search…"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                />
                            </InputGroup>
                        </div>

                        {/* Поля ID (BID / OID) и Author / Status — компактные */}
                        <div className="d-flex flex-wrap align-items-end gap-1">
                            <div style={{ width: 80 }}>
                                <small className="text-muted mb-1 d-none d-block">BID</small>
                                <Form.Control
                                    type="number"
                                    className="form-control-sm"
                                    placeholder="BID"
                                    value={bookId}
                                    onChange={(e) => setBookId(e.target.value)}
                                />
                            </div>
                            <div style={{ width: 80 }}>
                                <small className="text-muted mb-1 d-none d-block">OID</small>
                                <Form.Control
                                    type="number"
                                    className="form-control-sm"
                                    placeholder="OID"
                                    value={ownerId}
                                    onChange={(e) => setOwnerId(e.target.value)}
                                />
                            </div>
                            <div style={{ width: 140 }} className="d-none d-md-block">
                                <small className="text-muted mb-1 d-none d-block">Author</small>
                                <Form.Control
                                    type="text"
                                    className="form-control-sm"
                                    placeholder="Author"
                                    value={author}
                                    onChange={(e) => setAuthor(e.target.value)}
                                />
                            </div>

                        </div>

                        {/* ДАТЫ Created (from/to) — размеры как в AssignmentSearch (60px) */}
                        <div className="d-flex d-none align-items-end gap-1">
                            <div>
                                <small className="text-muted mb-1  d-block">Created</small>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={createdFrom ? new Date(createdFrom).toISOString().slice(0, 10) : ""}
                                    onChange={(e) => setCreatedFrom(e.target.value ? new Date(e.target.value) : null)}
                                    style={{ width: 60 }}
                                />
                            </div>
                            <div>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={createdTo ? new Date(createdTo).toISOString().slice(0, 10) : ""}
                                    onChange={(e) => setCreatedTo(e.target.value ? new Date(e.target.value) : null)}
                                    style={{ width: 60 }}
                                />
                            </div>
                        </div>

                        {/* Флаги — компактный блок чекбоксов (прячем на xs/sm) */}
                        <div className="d-none d-md-flex align-items-center gap-2 ms-1">
                            <Form.Check
                                type="checkbox"
                                id="flag-k"
                                label="K"
                                checked={booleanFilters.kindle_flag}
                                onChange={() => handleCheckboxChange("kindle_flag")}
                            />
                            <Form.Check
                                type="checkbox"
                                id="flag-ku"
                                label="KU"
                                checked={booleanFilters.kindle_unlimited_flag}
                                onChange={() => handleCheckboxChange("kindle_unlimited_flag")}
                            />
                            <Form.Check
                                type="checkbox"
                                id="flag-pb"
                                label="PB"
                                checked={booleanFilters.paper_back_flag}
                                onChange={() => handleCheckboxChange("paper_back_flag")}
                            />
                            <Form.Check
                                type="checkbox"
                                id="flag-hc"
                                label="HC"
                                checked={booleanFilters.hard_cover_flag}
                                onChange={() => handleCheckboxChange("hard_cover_flag")}
                            />
                            <Form.Check
                                type="checkbox"
                                id="flag-a"
                                label="A"
                                checked={booleanFilters.audio_flag}
                                onChange={() => handleCheckboxChange("audio_flag")}
                            />
                            <Form.Check
                                type="checkbox"
                                id="flag-cf"
                                label="CF"
                                checked={booleanFilters.has_cover_file}
                                onChange={() => handleCheckboxChange("has_cover_file")}
                            />
                            <Form.Check
                                type="checkbox"
                                id="flag-bf"
                                label="BF"
                                checked={booleanFilters.has_book_file}
                                onChange={() => handleCheckboxChange("has_book_file")}
                            />
                            <Form.Check
                                type="checkbox"
                                id="flag-msg"
                                label="MSG"
                                checked={booleanFilters.message_flag}
                                onChange={() => handleCheckboxChange("message_flag")}
                            />
                        </div>


                        {/* Per Page (PP) */}
                        <div className="ms-3">
                            <small className="text-muted mb-1 d-none d-block">PP</small>
                            <Form.Select
                                size="sm"
                                value={limit}
                                onChange={(e) => handleLimitChange(Number(e.target.value))}
                                style={{ width: 60 }}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </Form.Select>
                        </div>
                        <div className="d-flex justify-content-center gap-3 mt-3">
                            <AdminAmazonAvailabilityAudit />
                        </div>
                    </div>

                    {/* Таблица */}
                    {message && <p className="text-muted mt-3 text-center">{message}</p>}

                    {loading ? (
                        <div className="d-flex align-items-center gap-2">
                            <Spinner size="sm" /> <small>Loading…</small>
                        </div>
                    ) : (
                        <>
                            {books.length > 0 && (
                                <>
                                    <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '10px' }}>
                                        <Table striped bordered hover size="sm" style={{ minWidth: '1200px' }}>
                                            <thead className="small">
                                            <tr className="small">
                                                <th className="py-0 px-2">BID</th>
                                                <th className="py-0 px-2">Title</th>
                                                <th className="py-0 px-2 text-center">In</th>
                                                <th className="py-0 px-2 text-center">Out</th>
                                                <th className="py-0 px-2 text-center">Status</th>
                                                <th className="py-0 px-2 text-center">Staff</th>
                                                <th className="py-0 px-2 text-center">Ass</th>
                                                <th className="py-0 px-2 text-center">Language</th>
                                                <th className="py-0 px-2 text-center">Words</th>
                                                <th className="py-0 px-2 text-center">PB</th>
                                                <th className="py-0 px-2 text-center">KD</th>
                                                <th className="py-0 px-2 text-center">KU</th>
                                                <th className="py-0 px-2 text-center">HC</th>
                                                <th className="py-0 px-2 text-center">AU</th>
                                                <th className="py-0 px-2">OID</th>
                                                <th className="py-0 px-2">AMZ Name</th>
                                                <th className="py-0 px-2">Owner Email</th>
                                                <th className="py-0 px-2">Plan</th>
                                                <th className="py-0 px-2">Virt</th>
                                                <th className="py-0 px-2">USD</th>
                                                <th className="py-0 px-2">$AFF</th>



                                            </tr>
                                            </thead>
                                            <tbody className="text-muted small">
                                            {books?.length > 0 ? (
                                                books.map((book) => (
                                                    <tr
                                                        key={book.id}
                                                        className="align-middle small"
                                                        style={{cursor: "pointer"}} // Указываем, что строка кликабельна
                                                        onClick={(e) => {
                                                            if (!e.target.closest("button")) {
                                                                handleEditClick(book);
                                                            }
                                                        }}
                                                    >
                                                        <td className="text-start py-0 px-2">{book.id}</td>
                                                        <td className="text-start py-0 px-2">
                                                            {book?.title?.length > 46 ? book.title.substring(0, 30) + "..." : book.title}
                                                        </td>
                                                        <td className="py-0 px-2 text-center">{formatMbShort(book.book_file_original_size_bytes)}</td>
                                                        <td className="py-0 px-2 text-center">{formatMbShort(book.book_file_display_size_bytes || book.book_file_processed_size_bytes || book.book_file_size_bytes)}</td>


                                                        <td className="py-0 px-2 text-center">
                                                            <Badge bg={book.status === "NEW" ? "danger" : "secondary"}
                                                                   className={book.status === "NEW" ? "fw-normal" : "fw-normal"}>
                                                                {book.status}
                                                            </Badge>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">
                                                              <span className="small fw-semibold">
                                                                {book.message_flag ? "Yes" : "No"}
                                                            </span>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">{book?.assCount || "N/A"}</td>
                                                        <td className="py-0 px-2 text-center">{book.language || "N/A"}</td>
                                                        <td className="py-0 px-2 text-center">{book.total_words || "N/A"}</td>

                                                        <td className="py-0 px-2 text-center">
                                                             <span className="small">
                                                                {book.paper_back_flag ? "✔" : "✖"}
                                                            </span>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">
                                                             <span className="small">
                                                                {book.kindle_flag ? "✔" : "✖"}
                                                             </span>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">
                                                              <span className="small">
                                                                {book.kindle_unlimited_flag ? "✔" : "✖"}
                                                            </span>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">
                                                             <span className="small">
                                                                {book.hard_cover_flag ? "✔" : "✖"}
                                                             </span>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">
                                                            <span className="small">
                                                                {book.audio_flag ? "✔" : "✖"}
                                                            </span>
                                                        </td>
                                                        <td className="text-start py-0 px-2">{book?.userId || "N/A"}</td>
                                                        <td className="py-0 px-2 text-start">{book?.user?.amz_name?.slice(0, 25) || "N/A"} </td>
                                                        <td className="text-start py-0 px-2">{book?.user?.email?.slice(0, 25) || "N/A"}</td>
                                                        <td className="py-0 px-2 text-start">{book?.user?.tariff_plan || "N/A"}</td>
                                                        <td className="text-start py-0 px-2">{book?.user?.balance || "N/A"} Virt</td>
                                                        <td className="text-start py-0 px-2">
                                                            {Number.isFinite(Number(book?.user?.lr_balance))
                                                                ? `$${Number(book.user.lr_balance).toFixed(2)}`
                                                                : "N/A"}
                                                        </td>
                                                        <td className="text-start py-0 px-2">
                                                            {Number.isFinite(Number(book?.user?.affiliate_balance))
                                                                ? `$${Number(book.user.affiliate_balance).toFixed(2)}`
                                                                : "N/A"}
                                                        </td>

                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="21" className="text-center py-0 px-2">No books found</td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Пагинация — компактно как в AssignmentSearch */}
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="small text-muted">
                                            Total: {count} • Page {page} of {totalPages}
                                        </div>
                                        <Pagination size="sm" className="mb-0">
                                            <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page <= 1} />
                                            <Pagination.Item active>{page}</Pagination.Item>
                                            <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} />
                                        </Pagination>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {/* ===== Дубликаты (те же колонки, таблица под основной) ===== */}
                    {duplicates?.length > 0 && (
                        <>
                            <hr className="my-3" />
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Badge bg="danger" pill>{duplicates.length}</Badge>
                                <small className="text-muted">Duplicates by ASIN (same layout as above)</small>
                            </div>

                            <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '10px' }}>
                                <Table striped bordered hover size="sm" style={{ minWidth: '1200px' }}>
                                    <thead className="small">
                                    <tr className="small">
                                        <th className="py-0 px-2">BID</th>
                                        <th className="py-0 px-2">Title</th>
                                        <th className="py-0 px-2 text-center">In</th>
                                        <th className="py-0 px-2 text-center">Out</th>
                                        <th className="py-0 px-2 text-center">Status</th>
                                        <th className="py-0 px-2 text-center">Staff</th>
                                        <th className="py-0 px-2 text-center">Ass</th>
                                        <th className="py-0 px-2 text-center">Language</th>
                                        <th className="py-0 px-2 text-center">Words</th>
                                        <th className="py-0 px-2 text-center">PB</th>
                                        <th className="py-0 px-2 text-center">KD</th>
                                        <th className="py-0 px-2 text-center">KU</th>
                                        <th className="py-0 px-2 text-center">HC</th>
                                        <th className="py-0 px-2 text-center">AU</th>
                                        <th className="py-0 px-2">OID</th>
                                        <th className="py-0 px-2">AMZ Name</th>
                                        <th className="py-0 px-2">Owner Email</th>
                                        <th className="py-0 px-2">Plan</th>
                                        <th className="py-0 px-2">Virt</th>
                                        <th className="py-0 px-2">USD</th>
                                        <th className="py-0 px-2">$AFF</th>
                                    </tr>
                                    </thead>

                                    <tbody className="text-muted small">
                                    {duplicates.map((d) => {
                                        const duplicateInfo = d?.duplicate_info || {};
                                        const groupBooks = Array.isArray(duplicateInfo.books) && duplicateInfo.books.length
                                            ? duplicateInfo.books
                                            : [books.find((x) => x.id === d.id) || d];
                                        const b = groupBooks[0];
                                        const duplicateBooks = groupBooks.slice(1);

                                        const dupMsg =
                                            d?.duplicate_info?.message
                                            ?? (Array.isArray(d?.duplicate_info?.asins) && d.duplicate_info.asins.length
                                                ? 'Duplicates by ASINs: ' + d.duplicate_info.asins
                                                .map(a => `${a.value} (${(a.fields || []).join(', ')})`)
                                                .join('; ')
                                                : 'Duplicates detected');

                                        return (
                                            <React.Fragment key={`dup-wrap-${d.id}`}>
                                                {/* БАННЕР НАД КНИГОЙ */}
                                                <tr >
                                                    <td colSpan={21} className="py-0 px-2 bg-warning-subtle text-dark small">
                                                        <span className="fw-semibold me-2">Duplicate group:</span>
                                                        <span>{dupMsg}</span>
                                                        {Array.isArray(d?.duplicate_info?.bookIds) && d.duplicate_info.bookIds.length > 0 && (
                                                            <span className="ms-2 text-muted">
                        (bookIds: {d.duplicate_info.bookIds.join(', ')})
                      </span>
                                                        )}
                                                    </td>
                                                </tr>

                                                {/* СТРОКА С КНИГОЙ */}
                                                <tr
                                                    key={`dup-${d.id}`}
                                                    className="align-middle small"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={(e) => {
                                                        if (!e.target.closest("button")) handleEditClick(b, duplicateInfo);
                                                    }}
                                                >
                                                    <td className="text-start py-0 px-2">{b.id}</td>
                                                    <td className="text-start py-0 px-2">
                                                        {b?.title ? (b.title.length > 46 ? b.title.substring(0, 30) + "..." : b.title) : "N/A"}
                                                    </td>
                                                    <td className="py-0 px-2 text-center">{formatMbShort(b.book_file_original_size_bytes)}</td>
                                                    <td className="py-0 px-2 text-center">{formatMbShort(b.book_file_display_size_bytes || b.book_file_processed_size_bytes || b.book_file_size_bytes)}</td>
                                                    <td className="py-0 px-2 text-center">
                                                        <Badge bg={b.status === "NEW" ? "danger" : "secondary"} className="fw-normal">
                                                            {b.status || "N/A"}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-0 px-2 text-center">
                                                        <span className="small fw-semibold">{b.message_flag ? "Yes" : "No"}</span>
                                                    </td>
                                                    <td className="py-0 px-2 text-center">{b?.assCount ?? "N/A"}</td>
                                                    <td className="py-0 px-2 text-center">{b?.language || "N/A"}</td>
                                                    <td className="py-0 px-2 text-center">{b?.total_words || "N/A"}</td>
                                                    <td className="py-0 px-2 text-center"><span className="small">{b?.paper_back_flag ? "✔" : "✖"}</span></td>
                                                    <td className="py-0 px-2 text-center"><span className="small">{b?.kindle_flag ? "✔" : "✖"}</span></td>
                                                    <td className="py-0 px-2 text-center"><span className="small">{b?.kindle_unlimited_flag ? "✔" : "✖"}</span></td>
                                                    <td className="py-0 px-2 text-center"><span className="small">{b?.hard_cover_flag ? "✔" : "✖"}</span></td>
                                                    <td className="py-0 px-2 text-center"><span className="small">{b?.audio_flag ? "✔" : "✖"}</span></td>
                                                    <td className="text-start py-0 px-2">{b?.userId ?? "N/A"}</td>
                                                    <td className="py-0 px-2 text-start">{b?.user?.amz_name ? b.user.amz_name.slice(0, 25) : "N/A"}</td>
                                                    <td className="text-start py-0 px-2">{b?.user?.email ? b.user.email.slice(0, 25) : "N/A"}</td>
                                                    <td className="py-0 px-2 text-start">{b?.user?.tariff_plan || "N/A"}</td>
                                                    <td className="text-start py-0 px-2">{b?.user?.balance != null ? `${b.user.balance} Virt` : "N/A"}</td>
                                                    <td className="text-start py-0 px-2">
                                                        {Number.isFinite(Number(b?.user?.lr_balance)) ? `$${Number(b.user.lr_balance).toFixed(2)}` : "N/A"}
                                                    </td>
                                                    <td className="text-start py-0 px-2">
                                                        {Number.isFinite(Number(b?.user?.affiliate_balance)) ? `$${Number(b.user.affiliate_balance).toFixed(2)}` : "N/A"}
                                                    </td>
                                                </tr>
                                                {duplicateBooks.map((groupBook) => (
                                                    <tr
                                                        key={`dup-${d.id}-${groupBook.id}`}
                                                        className="align-middle small"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => {
                                                            if (!e.target.closest("button")) handleEditClick(groupBook, duplicateInfo);
                                                        }}
                                                    >
                                                        <td className="text-start py-0 px-2">{groupBook.id}</td>
                                                        <td className="text-start py-0 px-2">
                                                            {groupBook?.title ? (groupBook.title.length > 46 ? groupBook.title.substring(0, 30) + "..." : groupBook.title) : "N/A"}
                                                        </td>
                                                        <td className="py-0 px-2 text-center">{formatMbShort(groupBook.book_file_original_size_bytes)}</td>
                                                        <td className="py-0 px-2 text-center">{formatMbShort(groupBook.book_file_display_size_bytes || groupBook.book_file_processed_size_bytes || groupBook.book_file_size_bytes)}</td>
                                                        <td className="py-0 px-2 text-center">
                                                            <Badge bg={groupBook.status === "NEW" ? "danger" : "secondary"} className="fw-normal">
                                                                {groupBook.status || "N/A"}
                                                            </Badge>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">
                                                            <span className="small fw-semibold">{groupBook.message_flag ? "Yes" : "No"}</span>
                                                        </td>
                                                        <td className="py-0 px-2 text-center">{groupBook?.assCount ?? "N/A"}</td>
                                                        <td className="py-0 px-2 text-center">{groupBook?.language || "N/A"}</td>
                                                        <td className="py-0 px-2 text-center">{groupBook?.total_words || "N/A"}</td>
                                                        <td className="py-0 px-2 text-center"><span className="small">{groupBook?.paper_back_flag ? "✔" : "✖"}</span></td>
                                                        <td className="py-0 px-2 text-center"><span className="small">{groupBook?.kindle_flag ? "✔" : "✖"}</span></td>
                                                        <td className="py-0 px-2 text-center"><span className="small">{groupBook?.kindle_unlimited_flag ? "✔" : "✖"}</span></td>
                                                        <td className="py-0 px-2 text-center"><span className="small">{groupBook?.hard_cover_flag ? "✔" : "✖"}</span></td>
                                                        <td className="py-0 px-2 text-center"><span className="small">{groupBook?.audio_flag ? "✔" : "✖"}</span></td>
                                                        <td className="text-start py-0 px-2">{groupBook?.userId ?? "N/A"}</td>
                                                        <td className="py-0 px-2 text-start">{groupBook?.user?.amz_name ? groupBook.user.amz_name.slice(0, 25) : "N/A"}</td>
                                                        <td className="text-start py-0 px-2">{groupBook?.user?.email ? groupBook.user.email.slice(0, 25) : "N/A"}</td>
                                                        <td className="py-0 px-2 text-start">{groupBook?.user?.tariff_plan || "N/A"}</td>
                                                        <td className="text-start py-0 px-2">{groupBook?.user?.balance != null ? `${groupBook.user.balance} Virt` : "N/A"}</td>
                                                        <td className="text-start py-0 px-2">
                                                            {Number.isFinite(Number(groupBook?.user?.lr_balance)) ? `$${Number(groupBook.user.lr_balance).toFixed(2)}` : "N/A"}
                                                        </td>
                                                        <td className="text-start py-0 px-2">
                                                            {Number.isFinite(Number(groupBook?.user?.affiliate_balance)) ? `$${Number(groupBook.user.affiliate_balance).toFixed(2)}` : "N/A"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}


                </Card.Body>
            </Card>

            {/* Модалка редактирования книги */}
            {modalShow && (
                <EditBookAdmin
                    show={modalShow}
                    handleClose={handleModalClose}
                    bookData={selectedBook}
                    duplicateInfo={selectedDuplicateInfo}
                    onUpdate={() => selectedBook && refetchOneBook(selectedBook.id)}
                    onOpenUserBooks={onOpenUserBooks}
                    onOpenBookAssignments={onOpenBookAssignments}
                    onOpenUserAuthorAssignments={onOpenUserAuthorAssignments}
                    onOpenUserReviewerAssignments={onOpenUserReviewerAssignments}
                />
            )}
        </>

    );
};

export default BookSearch;
